import {
  cacheByProject,
  cacheBySession,
  cacheStats,
  summarizeCompactions,
} from "../analysis/cache";
import { classifyTurns } from "../analysis/classify";
import { build5hWindows, WINDOW_MS, weeklyRollup } from "../analysis/quota";
import {
  computeWaste,
  isPremiumModel,
  rootSessionId,
  type SessionAggregate,
} from "../analysis/waste";
import type { PriceTable } from "../pricing/pricing";
import { PHASES, type Phase, type SideEvent, type TurnEvent } from "../types";
import { basename, fmtDate, fmtDateTime, fmtPct, fmtTokens, fmtUsd, shortId } from "./format";

export interface ReportRange {
  days: number | null;
  project: string | null;
}

export interface ReportModel {
  generatedAt: string;
  priceTableVersion: string;
  range: { days: number | null; since: string | null; until: string; project: string | null };
  overview: {
    sessions: number;
    turns: number;
    subagentTurns: number;
    projects: number;
    totalTokens: number;
    tokens: { input: number; output: number; cacheRead: number; cacheWrite: number };
    totalCostUsd: number;
    unpricedTurns: number;
    unpricedModels: string[];
  };
  byModel: Array<{
    model: string;
    turns: number;
    input: number;
    output: number;
    cacheRead: number;
    cacheWrite: number;
    costUsd: number | null;
    costShare: number | null;
  }>;
  phaseMatrix: Array<{
    model: string;
    phases: Record<Phase, { turns: number; costUsd: number; wasteUsd: number }>;
  }>;
  waste: {
    totalUsd: number;
    explorationUsd: number;
    executionUsd: number;
    pctOfSpend: number | null;
    fastModeExtraUsd: number;
    assumption: string;
  };
  cache: {
    overall: { hitRate: number | null; input: number; cacheRead: number; cacheWrite: number };
    byProject: Array<{
      project: string;
      turns: number;
      hitRate: number | null;
      cacheRead: number;
      cacheWrite: number;
    }>;
    compactions: { total: number; tokensCompacted: number; sessionsAffected: number };
  };
  topSessions: Array<{
    sessionId: string;
    label: string;
    project: string;
    date: string;
    turns: number;
    costUsd: number;
    wasteUsd: number;
    antiPattern: string;
  }>;
  subagentLeaks: Array<{ agentType: string; model: string; runs: number; costUsd: number }>;
  quota: {
    windowHours: number;
    last7d: Array<{
      start: string;
      startTs: number;
      turns: number;
      totalTokens: number;
      costUsd: number;
    }>;
    weekly: Array<{ week: string; turns: number; totalTokens: number; costUsd: number }>;
    note: string;
  };
  recommendations: string[];
}

export const WASTE_EPSILON = 0.005;
const QUOTA_NOTE =
  "Estimated reconstruction of Anthropic's rolling 5h usage windows — not official quota accounting.";
const WASTE_ASSUMPTION =
  "Waste assumes the cheaper model would consume the same token counts; actual savings vary.";

export function buildReport(
  turns: TurnEvent[],
  events: SideEvent[],
  prices: PriceTable,
  range: ReportRange,
): ReportModel {
  const sessionIds = new Set(turns.map((t) => t.sessionId));
  const relevantEvents = events.filter((e) => sessionIds.has(e.sessionId));
  const classified = classifyTurns(turns, events);
  const waste = computeWaste(classified, prices);

  // ---- overview -----------------------------------------------------------
  const rootSessions = new Set<string>();
  const projects = new Set<string>();
  const tokens = { input: 0, output: 0, cacheRead: 0, cacheWrite: 0 };
  let subagentTurns = 0;
  let minTs = Infinity;
  let maxTs = 0;
  for (const t of turns) {
    rootSessions.add(rootSessionId(t));
    if (t.projectPath) projects.add(t.projectPath);
    tokens.input += t.usage.input;
    tokens.output += t.usage.output;
    tokens.cacheRead += t.usage.cacheRead;
    tokens.cacheWrite += t.usage.cacheWrite;
    if (t.isSidechain) subagentTurns++;
    if (t.timestamp > 0) {
      minTs = Math.min(minTs, t.timestamp);
      maxTs = Math.max(maxTs, t.timestamp);
    }
  }
  const totalTokens = tokens.input + tokens.output + tokens.cacheRead + tokens.cacheWrite;

  // ---- by model ------------------------------------------------------------
  interface ModelAgg {
    turns: number;
    input: number;
    output: number;
    cacheRead: number;
    cacheWrite: number;
    costUsd: number;
    unpriced: boolean;
  }
  const byModelMap = new Map<string, ModelAgg>();
  for (const t of turns) {
    const u = t.usage;
    if (u.input + u.output + u.cacheRead + u.cacheWrite === 0) continue; // synthetic placeholders
    let agg = byModelMap.get(t.model);
    if (!agg) {
      agg = {
        turns: 0,
        input: 0,
        output: 0,
        cacheRead: 0,
        cacheWrite: 0,
        costUsd: 0,
        unpriced: false,
      };
      byModelMap.set(t.model, agg);
    }
    agg.turns++;
    agg.input += u.input;
    agg.output += u.output;
    agg.cacheRead += u.cacheRead;
    agg.cacheWrite += u.cacheWrite;
    const cost = prices.turnCostUsd(t);
    if (cost === null) agg.unpriced = true;
    else agg.costUsd += cost;
  }
  const byModel = [...byModelMap.entries()]
    .map(([model, a]) => ({
      model,
      turns: a.turns,
      input: a.input,
      output: a.output,
      cacheRead: a.cacheRead,
      cacheWrite: a.cacheWrite,
      costUsd: a.unpriced ? null : a.costUsd,
      costShare: !a.unpriced && waste.totalCostUsd > 0 ? a.costUsd / waste.totalCostUsd : null,
    }))
    .sort((a, b) => (b.costUsd ?? 0) - (a.costUsd ?? 0) || b.turns - a.turns);

  // ---- phase matrix ----------------------------------------------------------
  const phaseMatrix = [...waste.matrix.entries()]
    .map(([model, phases]) => {
      const row = {} as Record<Phase, { turns: number; costUsd: number; wasteUsd: number }>;
      let total = 0;
      for (const phase of PHASES) {
        const cell = phases.get(phase);
        row[phase] = {
          turns: cell?.turns ?? 0,
          costUsd: cell?.costUsd ?? 0,
          wasteUsd: cell?.wasteUsd ?? 0,
        };
        total += row[phase].costUsd;
      }
      return { model, phases: row, total };
    })
    .sort((a, b) => b.total - a.total)
    .map(({ model, phases }) => ({ model, phases }));

  // ---- cache ---------------------------------------------------------------
  const overallCache = cacheStats(turns);
  const cacheProjects = [...cacheByProject(turns).entries()]
    .map(([project, s]) => ({
      // basename keeps the model identical whether projectPath is the real cwd
      // (local CLI) or the hashed id/name pair (synced server data).
      project: basename(project),
      turns: s.turns,
      hitRate: s.hitRate,
      cacheRead: s.cacheRead,
      cacheWrite: s.cacheWrite,
    }))
    .filter((p) => p.turns >= 10)
    .sort((a, b) => (a.hitRate ?? 1) - (b.hitRate ?? 1))
    .slice(0, 8);
  const compactions = summarizeCompactions(relevantEvents);

  // ---- top sessions ---------------------------------------------------------
  const cachePerSession = cacheBySession(turns);
  const topSessions = [...waste.perSession.values()]
    .sort((a, b) => b.wasteUsd - a.wasteUsd || b.costUsd - a.costUsd)
    .slice(0, 5)
    .map((s) => ({
      sessionId: s.sessionId,
      label: s.slug ?? shortId(s.sessionId),
      project: basename(s.project),
      date: fmtDate(s.firstTs === Infinity ? 0 : s.firstTs),
      turns: s.turns,
      costUsd: s.costUsd,
      wasteUsd: s.wasteUsd,
      antiPattern: dominantAntiPattern(s, cachePerSession.get(s.sessionId)?.hitRate ?? null),
    }));

  // ---- subagent leaks --------------------------------------------------------
  const agentTypeById = new Map<string, string>();
  for (const e of relevantEvents) {
    if (e.kind !== "agent_result") continue;
    const id = typeof e.data.agentId === "string" ? e.data.agentId : undefined;
    const type = typeof e.data.agentType === "string" ? e.data.agentType : undefined;
    if (id && type) agentTypeById.set(id, type);
  }
  interface ChildAgg {
    costUsd: number;
    premiumModel: string | null;
  }
  const children = new Map<string, ChildAgg>();
  for (const { turn } of classified) {
    if (!turn.agentId) continue;
    let child = children.get(turn.agentId);
    if (!child) {
      child = { costUsd: 0, premiumModel: null };
      children.set(turn.agentId, child);
    }
    const cost = prices.turnCostUsd(turn);
    if (cost !== null) child.costUsd += cost;
    if (!child.premiumModel && isPremiumModel(turn.model)) child.premiumModel = turn.model;
  }
  const leakGroups = new Map<
    string,
    { agentType: string; model: string; runs: number; costUsd: number }
  >();
  for (const [agentId, child] of children) {
    if (!child.premiumModel) continue;
    const agentType = agentTypeById.get(agentId) ?? "subagent";
    const key = `${agentType} ${child.premiumModel}`;
    let g = leakGroups.get(key);
    if (!g) {
      g = { agentType, model: child.premiumModel, runs: 0, costUsd: 0 };
      leakGroups.set(key, g);
    }
    g.runs++;
    g.costUsd += child.costUsd;
  }
  const subagentLeaks = [...leakGroups.values()].sort((a, b) => b.costUsd - a.costUsd);

  // ---- quota -----------------------------------------------------------------
  const windows = build5hWindows(turns, prices);
  const last7dStart = maxTs > 0 ? maxTs - 7 * 86400000 : 0;
  const last7d = windows
    .filter((w) => w.start >= last7dStart)
    .map((w) => ({
      start: fmtDateTime(w.start),
      startTs: w.start,
      turns: w.turns,
      totalTokens: w.totalTokens,
      costUsd: w.costUsd,
    }));
  const weekly = weeklyRollup(turns, prices).map((w) => ({
    week: w.weekKey,
    turns: w.turns,
    totalTokens: w.totalTokens,
    costUsd: w.costUsd,
  }));

  // ---- recommendations ---------------------------------------------------------
  const spanDays = Math.max(1, (maxTs - (minTs === Infinity ? maxTs : minTs)) / 86400000);
  const monthly = (x: number): number => (x / spanDays) * 30;
  const recommendations: string[] = [];
  if (monthly(waste.wasteByPhase.exploration) >= 1) {
    recommendations.push(
      `Delegate exploration to a Haiku-pinned subagent (frontmatter: model: haiku, effort: low) — ` +
        `roughly ${fmtUsd(monthly(waste.wasteByPhase.exploration))}/month of premium-model exploration to reclaim.`,
    );
  }
  if (monthly(waste.wasteByPhase.execution) >= 1) {
    recommendations.push(
      `Run edit/build-heavy work on Sonnet (settings "model": "opusplan", or a sonnet implementer subagent) — ` +
        `roughly ${fmtUsd(monthly(waste.wasteByPhase.execution))}/month spent running premium models in execution.`,
    );
  }
  const leakRuns = subagentLeaks.reduce((n, l) => n + l.runs, 0);
  if (leakRuns > 0) {
    recommendations.push(
      `${leakRuns} subagent run(s) executed on a premium model — pin model: (haiku/sonnet) in .claude/agents/*.md frontmatter so delegation actually saves money.`,
    );
  }
  if (overallCache.hitRate !== null && overallCache.hitRate < 0.7) {
    recommendations.push(
      `Cache hit rate is ${fmtPct(overallCache.hitRate)} — cold starts repay your context at full price. Prefer resuming sessions (claude --continue) over starting fresh ones.`,
    );
  }
  if (monthly(waste.fastModeExtraUsd) >= 1) {
    recommendations.push(
      `Fast mode premium added ${fmtUsd(waste.fastModeExtraUsd)} in this window — reserve /fast for latency-critical work.`,
    );
  }
  if (compactions.total >= 5) {
    recommendations.push(
      `${compactions.total} context compaction(s) re-summarized ~${fmtTokens(compactions.tokensCompacted)} tokens — split long sessions or push exploration into subagents to keep the main context lean.`,
    );
  }

  return {
    generatedAt: new Date().toISOString(),
    priceTableVersion: prices.version,
    range: {
      days: range.days,
      since: minTs === Infinity ? null : new Date(minTs).toISOString(),
      until: new Date(maxTs > 0 ? maxTs : Date.now()).toISOString(),
      project: range.project,
    },
    overview: {
      sessions: rootSessions.size,
      turns: turns.length,
      subagentTurns,
      projects: projects.size,
      totalTokens,
      tokens,
      totalCostUsd: waste.totalCostUsd,
      unpricedTurns: waste.unpricedTurns,
      unpricedModels: waste.unpricedModels,
    },
    byModel,
    phaseMatrix,
    waste: {
      totalUsd: waste.totalWasteUsd,
      explorationUsd: waste.wasteByPhase.exploration,
      executionUsd: waste.wasteByPhase.execution,
      pctOfSpend: waste.totalCostUsd > 0 ? waste.totalWasteUsd / waste.totalCostUsd : null,
      fastModeExtraUsd: waste.fastModeExtraUsd,
      assumption: WASTE_ASSUMPTION,
    },
    cache: {
      overall: {
        hitRate: overallCache.hitRate,
        input: overallCache.input,
        cacheRead: overallCache.cacheRead,
        cacheWrite: overallCache.cacheWrite,
      },
      byProject: cacheProjects,
      compactions: {
        total: compactions.total,
        tokensCompacted: compactions.tokensCompacted,
        sessionsAffected: compactions.bySession.size,
      },
    },
    topSessions,
    subagentLeaks,
    quota: { windowHours: WINDOW_MS / 3600000, last7d, weekly, note: QUOTA_NOTE },
    recommendations,
  };
}

function dominantAntiPattern(s: SessionAggregate, hitRate: number | null): string {
  const parts: string[] = [];
  if (s.wasteExplorationUsd > WASTE_EPSILON && s.wasteExplorationUsd >= s.wasteExecutionUsd) {
    parts.push(`premium model on exploration (${fmtUsd(s.wasteExplorationUsd)})`);
  } else if (s.wasteExecutionUsd > WASTE_EPSILON) {
    parts.push(`premium model on execution (${fmtUsd(s.wasteExecutionUsd)})`);
  }
  if (parts.length === 0 && hitRate !== null && hitRate < 0.5 && s.turns >= 20) {
    parts.push(`low cache reuse (${fmtPct(hitRate)} hit rate)`);
  }
  return parts.length > 0 ? (parts[0] as string) : "—";
}
